"use client";

import { Cpu, Crosshair, Layers, ScanLine, Shield, Sparkles, Target, Wrench } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Standards() {
  const heading = useRevealOnScroll<HTMLHeadingElement>({
    threshold: 0.2
  });
  const subParagraph = useRevealOnScroll<HTMLParagraphElement>({
    threshold: 0.2
  });
  const eyebrow = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const grid = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const footer = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const headingWords = ["Fundamenty", "technologii", "i", "precyzji"];
  const features = [{
    title: "Ciężki sprzęt budowlany",
    description: "Dysponujemy nowoczesną flotą maszyn budowlanych, zapewniającą szybkość i najwyższą jakość prac ziemnych oraz wykopów na każdym terenie.",
    spec: "Wydajność · Precyzja · Niezawodność",
    icon: Crosshair,
    span: "lg:col-span-5",
    size: "large"
  }, {
    title: "Przyłącza wodno-kanalizacyjne",
    description: "Profesjonalne wykonawstwo instalacji podziemnych zgodnie z normami budowlanymi i wymogami technicznymi dla domów jednorodzinnych.",
    spec: "Szczelność · Zgodność z projektem",
    icon: Layers,
    span: "lg:col-span-4",
    size: "medium"
  }, {
    title: "Niwelacja terenu",
    description: "Dokładne przygotowanie działki pod budowę, niwelacja i kształtowanie terenu z użyciem zaawansowanego sprzętu pomiarowego.",
    spec: "Dokładność pomiarowa",
    icon: ScanLine,
    span: "lg:col-span-3",
    size: "compact"
  }, {
    title: "Instalacje elektryczne",
    description: "Kompleksowe przygotowanie wykopów i instalacji podziemnych dla przyłączy elektrycznych.",
    spec: "Bezpieczeństwo · Standardy",
    icon: Target,
    span: "lg:col-span-3",
    size: "compact"
  }, {
    title: "Płyty fundamentowe",
    description: "Solidne fundamenty i płyty fundamentowe, które stanowią stabilną bazę dla każdej inwestycji budowlanej na Śląsku.",
    spec: "Trwałość · Izolacja",
    icon: Cpu,
    span: "lg:col-span-4",
    size: "medium"
  }, {
    title: "Prace brukarskie",
    description: "Układanie kostki brukowej, podjazdy, parkingi i chodniki z dbałością o każdy detal wykończenia.",
    spec: "Estetyka · Wytrzymałość",
    icon: Shield,
    span: "lg:col-span-2",
    size: "compact"
  }, {
    title: "Dokumentacja techniczna",
    description: "Pełna dokumentacja i nadzór nad realizacją prac ziemnych i przyłączy.",
    spec: "Pełna przejrzystość",
    icon: Wrench,
    span: "lg:col-span-2",
    size: "compact"
  }];
  return <section data-section-id="5137" id="roboty-ziemne-i-przyłącza" className="dark bg-background text-foreground py-24 md:py-36 lg:py-44 px-6 md:px-10 lg:px-16 overflow-hidden">
      <div className="max-w-7xl mx-auto">
        {}
        <div className="grid grid-cols-12 gap-x-6 gap-y-10 mb-16 md:mb-24">
          <div ref={eyebrow.ref} className={`col-span-12 lg:col-start-2 lg:col-span-10 transition-all duration-700 ease-out ${eyebrow.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
            <div className="flex items-center gap-3 font-mono text-xs tracking-[0.2em] uppercase text-[var(--chart-3)]">
              <span className="inline-block w-8 h-px bg-[var(--chart-3)]" />
              <span>BUDSYS · JAKOŚĆ</span>
            </div>
          </div>

          <h2 ref={heading.ref} className="col-span-12 lg:col-start-2 lg:col-span-8 font-default font-bold text-4xl sm:text-5xl md:text-6xl lg:text-[64px] leading-[1.05] tracking-tight text-foreground">
            {headingWords.map((word, i) => <span key={i} data-index={i} className="inline-block overflow-hidden align-bottom mr-[0.25em]">
                <span className={`inline-block transition-all ease-out ${heading.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-full"}`} style={{
              transitionDuration: "800ms",
              transitionDelay: `${i * 90}ms`
            }}>
                  {word}
                </span>
              </span>)}
            <span className="inline-block overflow-hidden align-bottom">
              <span className={`inline-block italic font-light text-[var(--chart-3)] transition-all ease-out ${heading.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-full"}`} style={{
              transitionDuration: "800ms",
              transitionDelay: `${headingWords.length * 90}ms`
            }}>
                budowy.
              </span>
            </span>
          </h2>

          <p ref={subParagraph.ref} className={`col-span-12 lg:col-start-2 lg:col-span-6 text-base text-foreground/70 leading-relaxed font-default transition-all duration-700 delay-300 ease-out ${subParagraph.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
            Każda solidna inwestycja zaczyna się od profesjonalnego przygotowania terenu. W BudSys łączymy nowoczesny park maszynowy z wieloletnim doświadczeniem, zapewniając fundamenty, które przetrwają pokolenia.
          </p>
        </div>

        {}
        <div ref={grid.ref} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-5 md:gap-6">
          {features.map((feature, i) => {
          const Icon = feature.icon;
          const isLarge = feature.size === "large";
          const isMedium = feature.size === "medium";
          return <div key={i} data-index={i} className={`group relative ${feature.span} rounded-lg border border-border/60 bg-foreground/[0.03] p-6 md:p-7 lg:p-8 transition-all duration-500 ease-out hover:border-[var(--chart-3)] hover:bg-foreground/[0.05] ${isLarge ? "min-h-[280px] md:min-h-[340px]" : isMedium ? "min-h-[240px] md:min-h-[280px]" : "min-h-[200px] md:min-h-[240px]"} ${grid.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`} style={{
            transitionDelay: grid.isVisible ? `${i * 80}ms` : "0ms",
            transitionDuration: "700ms"
          }}>
                {}
                <div className="absolute top-5 right-5 flex items-center gap-2">
                  <span className="font-mono text-[10px] tracking-widest text-foreground/40">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="w-1.5 h-1.5 rounded-lg bg-[var(--chart-3)]" />
                </div>

                {}
                <div className={`${isLarge ? "w-20 h-20" : "w-12 h-12"} flex items-center justify-center rounded-lg border border-border/50 bg-background/40 text-foreground/70 transition-colors duration-500 group-hover:text-[var(--chart-3)] group-hover:border-[var(--chart-3)]/40 mb-6`}>
                  <Icon className={isLarge ? "w-9 h-9" : "w-5 h-5"} strokeWidth={1.25} />
                </div>

                {}
                <div className="flex flex-col">
                  <h3 className={`font-default font-bold ${isLarge ? "text-2xl md:text-[28px]" : isMedium ? "text-xl md:text-2xl" : "text-lg md:text-xl"} leading-tight text-foreground transition-colors duration-500 group-hover:text-[var(--chart-3)] mb-3`}>
                    {feature.title}
                  </h3>

                  <p className={`text-sm text-foreground/70 leading-relaxed mb-5 ${!isLarge && !isMedium ? "line-clamp-3" : ""}`}>
                    {feature.description}
                  </p>

                  <div className="mt-auto pt-4 border-t border-border/40">
                    <span className="font-mono text-[11px] tracking-wider uppercase text-foreground/50">
                      {feature.spec}
                    </span>
                  </div>
                </div>
              </div>;
        })}
        </div>

        {}
        <div ref={footer.ref} className={`mt-20 md:mt-28 flex items-center justify-center transition-all duration-700 ease-out ${footer.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <div className="flex items-center gap-3 text-foreground/60">
            <Sparkles className="w-3.5 h-3.5 text-[var(--chart-3)]" strokeWidth={1.5} />
            <p className="font-mono text-xs sm:text-sm tracking-[0.2em] uppercase text-center">
              <span>Solidny sprzęt</span>
              <span className="mx-3 text-[var(--chart-3)]">·</span>
              <span>Profesjonalne podejście</span>
              <span className="mx-3 text-[var(--chart-3)]">·</span>
              <span>Terminowa realizacja</span>
            </p>
            <Sparkles className="w-3.5 h-3.5 text-[var(--chart-3)]" strokeWidth={1.5} />
          </div>
        </div>
      </div>
    </section>;
}